import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { BellRing, Play, CheckCircle2, XCircle, CalendarOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const RESPONSE_LABELS = {
  pending: { label: 'Bíður svars', cls: 'bg-amber-100 text-amber-800' },
  sick: { label: 'Veikur', cls: 'bg-red-100 text-red-800' },
  vacation: { label: 'Orlof (bíður samþykktar)', cls: 'bg-blue-100 text-blue-800' },
  other: { label: 'Önnur fjarvera (bíður)', cls: 'bg-purple-100 text-purple-800' },
  clock_correction: { label: 'Leiðrétting bíður samþykktar', cls: 'bg-green-100 text-green-800' },
};

export default function Attendance({ user, onLogout }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [settings, setSettings] = useState({ enabled: false, delay: 30 });
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ovRes, ccRes, stRes] = await Promise.all([
        axios.get(`${API}/attendance/overview`, { params: { date }, headers: authHeaders() }),
        axios.get(`${API}/clock-corrections`, { params: { status: 'pending' }, headers: authHeaders() }),
        axios.get(`${API}/settings`, { headers: authHeaders() }),
      ]);
      setRows(ovRes.data.rows || []);
      setCorrections(ccRes.data || []);
      setSettings({
        enabled: stRes.data?.missing_attendance_notifications_enabled ?? true,
        delay: stRes.data?.missing_attendance_notification_delay_minutes ?? 30,
        adminEnabled: stRes.data?.admin_notifications_enabled ?? true,
        adminEmail: stRes.data?.admin_notification_email ?? '',
      });
    } catch (e) {
      toast.error('Villa við að sækja mætingaryfirlit');
    }
    setLoading(false);
  }, [date]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveSettings = async (next) => {
    try {
      await axios.put(`${API}/settings`, {
        missing_attendance_notifications_enabled: next.enabled,
        missing_attendance_notification_delay_minutes: next.delay,
        admin_notifications_enabled: next.adminEnabled,
        admin_notification_email: next.adminEmail || '',
      }, { headers: authHeaders() });
      setSettings(next);
      toast.success('Stillingar vistaðar');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Villa við vistun');
    }
  };

  const runCheck = async () => {
    try {
      const res = await axios.post(`${API}/attendance/run-check`, { force: true }, { headers: authHeaders() });
      toast.success(`Athugun keyrð – ${res.data.sent?.length || 0} tilkynningar sendar`);
      fetchAll();
    } catch (e) {
      toast.error('Villa við athugun');
    }
  };

  const cancelShift = async (employeeId) => {
    if (!window.confirm('Fella niður vakt þessa dags fyrir starfsmanninn?')) return;
    try {
      await axios.post(`${API}/attendance/schedule-override`, {
        employee_id: employeeId, date, status: 'cancelled',
      }, { headers: authHeaders() });
      toast.success('Vakt felld niður');
      fetchAll();
    } catch (e) {
      toast.error('Villa');
    }
  };

  const decideCorrection = async (id, action) => {
    try {
      await axios.post(`${API}/clock-corrections/${id}/${action}`, {}, { headers: authHeaders() });
      toast.success(action === 'approve' ? 'Leiðrétting samþykkt – innstimplun skráð' : 'Hafnað');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Villa');
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="attendance-admin-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BellRing className="h-6 w-6" /> Mætingareftirlit
          </h1>
          <div className="flex items-center gap-2">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="w-40" data-testid="attendance-date-input" />
            <Button variant="outline" onClick={runCheck} data-testid="attendance-run-check-button">
              <Play className="h-4 w-4 mr-1" /> Keyra athugun
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Stillingar</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap items-center gap-6 text-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={settings.enabled}
                onChange={(e) => saveSettings({ ...settings, enabled: e.target.checked })}
                data-testid="attendance-enabled-checkbox" />
              Sjálfvirkar mætingartilkynningar virkar
            </label>
            <label className="flex items-center gap-2">
              Senda eftir
              <select value={settings.delay}
                onChange={(e) => saveSettings({ ...settings, delay: parseInt(e.target.value) })}
                className="border rounded px-2 py-1" data-testid="attendance-delay-select">
                {[15, 30, 45, 60].map((m) => <option key={m} value={m}>{m} mín</option>)}
              </select>
              frá upphafi vaktar
            </label>
            {!settings.enabled && (
              <span className="text-amber-600">Óvirkt – engar sjálfvirkar tilkynningar sendar</span>
            )}
            <div className="w-full border-t pt-3 flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={settings.adminEnabled}
                  onChange={(e) => saveSettings({ ...settings, adminEnabled: e.target.checked })}
                  data-testid="admin-notifications-checkbox" />
                Tölvupóstur á stjórnanda við veikindatilkynningum og self-service breytingum
              </label>
              <label className="flex items-center gap-2">
                Netfang:
                <Input type="text" value={settings.adminEmail}
                  onChange={(e) => setSettings({ ...settings, adminEmail: e.target.value })}
                  onBlur={() => saveSettings(settings)}
                  placeholder="sjálfgefið: netföng stjórnenda"
                  className="w-72 h-8"
                  data-testid="admin-notification-email-input" />
              </label>
            </div>
          </CardContent>
        </Card>

        {corrections.length > 0 && (
          <Card className="border-green-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Leiðréttingar á gleymdum stimplunum – bíða samþykktar</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {corrections.map((cc) => (
                <div key={cc.id} className="flex flex-wrap items-center justify-between gap-2 border rounded p-2 text-sm"
                  data-testid={`correction-row-${cc.id}`}>
                  <span>
                    <b>{cc.employee_name || cc.user_id}</b> – {cc.date} – mætti kl. <b>{cc.requested_clock_in}</b>
                    <span className="text-xs text-gray-500 ml-2">(sjálfsafgreiðsluhlekkur)</span>
                  </span>
                  <span className="flex gap-2">
                    <Button size="sm" className="h-7 bg-green-600 hover:bg-green-700"
                      onClick={() => decideCorrection(cc.id, 'approve')} data-testid={`correction-approve-${cc.id}`}>
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Samþykkja
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-red-600"
                      onClick={() => decideCorrection(cc.id, 'reject')} data-testid={`correction-reject-${cc.id}`}>
                      <XCircle className="h-3 w-3 mr-1" /> Hafna
                    </Button>
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Staða dagsins {date}</CardTitle></CardHeader>
          <CardContent>
            {loading && <p className="text-sm text-gray-500">Sæki...</p>}
            <div className="space-y-2">
              {rows.map((r) => {
                const resp = r.notification ? RESPONSE_LABELS[r.notification.response_status] : null;
                return (
                  <div key={r.employee_id} className="border rounded-lg p-3 text-sm flex flex-wrap items-center justify-between gap-2"
                    data-testid={`attendance-row-${r.employee_id}`}>
                    <div>
                      <b>{r.name}</b>
                      <span className="ml-2 text-gray-500">
                        {r.shift_status === 'off_day' && 'Á ekki vakt'}
                        {r.shift_status === 'cancelled' && 'Vakt felld niður'}
                        {r.shift_status === 'no_shift_time' && 'Enginn vaktartími skráður'}
                        {(r.shift_status === 'scheduled' || r.shift_status === 'moved') &&
                          `Vakt: ${r.shift_start}${r.shift_end ? `–${r.shift_end}` : ''}`}
                      </span>
                      <span className="ml-2">
                        {r.clock_in
                          ? <Badge className="bg-green-100 text-green-800">Innstimplun: {r.clock_in.slice(11, 16)}</Badge>
                          : (r.shift_status === 'scheduled' || r.shift_status === 'moved')
                            ? <Badge className="bg-red-100 text-red-800">Innstimplun: Engin</Badge>
                            : null}
                      </span>
                      {r.absence && !r.clock_in && (
                        <Badge variant="outline" className="ml-1">{r.absence.startsWith('leave_') ? 'Fjarvera skráð' : 'Leiðrétting í bið'}</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {r.notification && (
                        <span className="text-xs text-gray-500">
                          Tilkynning send: {r.notification.sent_at?.slice(11, 16)} ({r.notification.language === 'en' ? 'English' : 'Íslenska'})
                        </span>
                      )}
                      {resp && <Badge className={resp.cls} data-testid={`attendance-response-${r.employee_id}`}>{resp.label}</Badge>}
                      {(r.shift_status === 'scheduled' || r.shift_status === 'moved') && (
                        <Button size="sm" variant="ghost" className="h-7 text-xs text-gray-500"
                          onClick={() => cancelShift(r.employee_id)} data-testid={`cancel-shift-${r.employee_id}`}>
                          <CalendarOff className="h-3 w-3 mr-1" /> Fella niður vakt
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
              {!loading && rows.length === 0 && <p className="text-sm text-gray-500">Engir starfsmenn.</p>}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
